<style lang="scss">
  @use "../../../../assets/css/global.scss" as *;
  @use "../../../../assets/css/_colors" as *;

  #tasks {
    width: 100%;
    background: #fff;
    display: grid;
    gap: 1rem;
    border-radius: 0.5rem;
    grid-template-rows: auto 1fr;
    overflow: hidden;

    .group-header {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 1rem;
      align-items: center;
      padding: 1.5rem;
      top: 10px;
      position: relative;

      .group-title {
        align-self: start;
        h2 {
          color: $text-black;
          font-size: 1.4rem;
          margin: 0;
        }
        p {
          color: $text-gray;
          margin: 0.8rem 0 0;
          font-size: 0.9rem;
        }
      }
      .duration-text {
        color: $text-gray;
        font-weight: 700;
        font-size: 0.9rem;
        padding-right: 2rem;
      }
    }

    #taskDetails {
      overflow-y: auto;
      display: grid;
      gap: 1rem;
      align-content: start;
      position: relative;
      height: 100%;

      &::before {
        height: 25px;
        content: "";
        position: sticky;
        top: 0;
        background: white;
        border-top: 1px solid $light-gray;
      }

      &.scrolled::before {
        height: 25px;
        z-index: 1;
        pointer-events: none;
        border-bottom: none;
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent);
      }

      #taskDetailsContainer {
        display: grid;
        gap: 0.4rem;
        position: relative;
        top: -35px;
        padding: 1rem 1.5rem;
      }

      #skeletonDetailsContainer {
        display: grid;
        gap: 1.2rem;
        padding: 1rem 1.5rem;
      }
    }

    .task-subgroup-header-wrapper {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 1rem;
      position: relative;
      scroll-margin-bottom: 2rem;

      &::after {
        content: "";
        position: absolute;
        left: 11px;
        top: 0px;
        bottom: -25px;
        width: 2px;
        background-color: $secondary-border-color;
        z-index: 0;
        border-radius: 1rem;
      }

      &.completed::after {
        background-color: $turquoise;
      }

      &.pending::after {
        background-color: $orange;
      }

      .task-subgroup-header {
        margin: 0;
        padding: 1rem 1rem calc(1rem - 10px) 1rem;
        color: $text-black;
        font-family: Lato;
        font-size: 1.1rem;
        font-style: normal;
        font-weight: 700;
        line-height: 1.5;
        letter-spacing: 0.32px;
        border-top: 1px solid $light-gray;
      }
    }
  }
</style>

<script>
  import { onMount } from "@lifecycle";
  import { trainingHubTaskStore } from "../../../api-stores/training-hub-store";
  import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import { interactiveTasks } from "./stores";

  import SkeletonLoader from "./Components/SkeletonLoader.svelte";
  import TrainingHubIcon from "./Components/TrainingHubIcon.svelte";
  import TaskDetail from "./TaskDetail/TaskDetail.svelte";

  let isScrolled = false;
  const { meta } = trainingHubTaskStore;

  onMount(async () => {
    await userStore.once();
  });

  let taskDetails;

  $: lastNonOptionalTask = [...$interactiveTasks.tasks]
    .reverse()
    .find((task) => !task.optional);

  $: firstOptionalTask = $interactiveTasks.tasks.find((task) => task.optional);

  $: if (lastNonOptionalTask && firstOptionalTask) {
    lastNonOptionalTask.isLastNonOptional = true;
    firstOptionalTask.isFirstOptional = true;
  }
  $: if (taskDetails)
    taskDetails.onscroll = () => {
      isScrolled = taskDetails.scrollTop > 0;
    };

  $: s = $interactiveTasks.selectedTaskGroup.duration === 1 ? "" : "s";

  $: durationText = $interactiveTasks.selectedTaskGroup.remainingTasks
    ? `About ${$interactiveTasks.selectedTaskGroup.duration} min${s}`
    : "Completed";

  $: headerIcon =
    $interactiveTasks.selectedTaskGroup.icon || "sourcewhale-logo";

  // Adds the 'isNewSubgroup' attribute to the first task in each subgroup
  $: {
    let previousSubgroupId = null;
    $interactiveTasks.tasks.forEach((task) => {
      task.isNewSubgroup = false;
      if (task.taskSubgroupId && task.taskSubgroupId !== previousSubgroupId) {
        task.isNewSubgroup = true;
      }
      previousSubgroupId = task.taskSubgroupId;
    });
  }
</script>

<section id="tasks">
  {#if !$meta.firstFetchDone}
    <SkeletonLoader type="header" />
  {:else}
    <div class="group-header">
      <TrainingHubIcon icon={headerIcon} size="large" />
      <div class="group-title">
        <h2>
          {$interactiveTasks.selectedTaskGroup.groupTitle ||
            "Let's Get Set Up for Success!"}
        </h2>
        <p>
          {$interactiveTasks.selectedTaskGroup.groupDescription ||
            "Complete the tasks below to get yourself up and running with SourceWhale."}
        </p>
      </div>
      <span class="duration-text">{durationText}</span>
    </div>
  {/if}
  <section id="taskDetails" bind:this={taskDetails} class:scrolled={isScrolled}>
    <div id="taskDetailsContainer">
      {#if !$meta.firstFetchDone}
        <div id="skeletonDetailsContainer">
          <SkeletonLoader type="task" count={6} />
        </div>
      {:else}
        {#each $interactiveTasks.tasks as task}
          {#if task.isNewSubgroup}
            <article class="task-subgroup-header-wrapper">
              <div style="width: 24px;"></div>
              <h2 class="task-subgroup-header">
                {task.taskSubgroupId}
              </h2>
            </article>
          {/if}
          <TaskDetail {task} />
        {/each}
      {/if}
    </div>
  </section>
</section>
